// lib/cookies.ts

interface CookieOptions {
  expires?: Date | number
  maxAge?: number
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
}

const SESSION_COOKIE_TTL = 1000 * 60 * 60 * 24 * 7 // 7 days

const cookies = {
  //============================= base ============================== //
  set: (name: string, value: string, options: CookieOptions = {}): void => {
    const { expires, maxAge, path = '/', domain, secure, sameSite } = options

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

    if (expires instanceof Date) {
      cookieString += `; expires=${expires.toUTCString()}`
    } else if (typeof maxAge === 'number') {
      cookieString += `; max-age=${maxAge}`
    }

    cookieString += `; path=${path}`

    if (domain) {
      cookieString += `; domain=${domain}`
    }
    if (secure) {
      cookieString += '; secure'
    }
    if (sameSite) {
      cookieString += `; samesite=${sameSite}`
    }

    document.cookie = cookieString
  },

  get: (name: string): string | null => {
    const nameEQ = encodeURIComponent(name) + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }
    return null
  },

  remove: (name: string, path = '/'): void => {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`
  },

  //============================= json cookies ============================== //

  setJSONCookie: (name: string, value: any, options: CookieOptions = {}): void => {
    const jsonValue = JSON.stringify(value)
    cookies.set(name, jsonValue, options)
  },

  getJSONCookie: (name: string): any | null => {
    const cookieValue = cookies.get(name)
    if (cookieValue === null) return null
    try {
      return JSON.parse(cookieValue)
    } catch (e) {
      console.error('Error parsing JSON cookie:', e)
      return null
    }
  },

  //============================= session cookies ============================== //

  setAppSessionId: (appID: string, sessionId: string, ttl = SESSION_COOKIE_TTL): void => {
    cookies.set(`session_id-${appID}`, sessionId, {
      expires: new Date(Date.now() + ttl),
      path: '/',
      secure: !import.meta.env.DEV,
      sameSite: 'lax',
    })
  },

  getAppSessionId: (appID: string): string | null => {
    const sessionId = cookies.get(`session_id-${appID}`)
    if (!sessionId) return null
    return sessionId
  },

  removeAppSessionId: (appID: string): void => {
    cookies.remove(`session_id-${appID}`)
  },
}

export default cookies
